import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";
import UFOSegment from "@atlassian/wac-ufo-segment";

import {
    Adg4DraggableTab,
    Adg4DraggableTabList,
    Adg4TabPanel,
    Adg4Tabs
} from "@/components/adg4/tabs/src";
import { DataEventProps } from "@/libraries/wpl/foundations/src";
import { Row } from "@/libraries/wpl/foundations/src";

import { Integrations } from "./integrations";
import {
    SoftwareTabContent,
    ProductManagementTabContent,
    MarketingTabContent,
    ProjectManagementTabContent,
    DesignTabContent,
    ITTabContent
} from "./tab-contents";

type TabContents = {
    id: string;
    title: string;
    label: string;
    component: React.ReactNode | (() => React.ReactNode);
};

const Tab = ({
    title,
    isSelected,
    dataEventProps
}: {
    title: string;
    isSelected?: boolean;
    dataEventProps: DataEventProps;
}) => {
    return (
        <button
            css={[tabStyles, isSelected && currentStyle]}
            {...dataEventProps}
        >
            {title}
        </button>
    );
};

export const TabsSection = () => {
    const intl = useIntl();

    const tabs: TabContents[] = [
        {
            id: "tab1",
            title: intl.formatMessage({
                id: "HomePageTemplateV2.tabsSection.label.tab1",
                defaultMessage: "Software"
            }),
            label: "Software",
            component: SoftwareTabContent
        },
        {
            id: "tab2",
            title: intl.formatMessage({
                id: "HomePageTemplateV2.tabsSection.label.tab2",
                defaultMessage: "Product management"
            }),
            label: "Product management",
            component: ProductManagementTabContent
        },
        {
            id: "tab3",
            title: intl.formatMessage({
                id: "HomePageTemplateV2.tabsSection.label.tab3",
                defaultMessage: "Marketing"
            }),
            label: "Marketing",
            component: MarketingTabContent
        },
        {
            id: "tab4",
            title: intl.formatMessage({
                id: "HomePageTemplateV2.tabsSection.label.tab4",
                defaultMessage: "Project management"
            }),
            label: "Project management",
            component: ProjectManagementTabContent
        },
        {
            id: "tab5",
            title: intl.formatMessage({
                id: "HomePageTemplateV2.tabsSection.label.tab5",
                defaultMessage: "Design"
            }),
            label: "Design",
            component: DesignTabContent
        },
        {
            id: "tab6",
            title: intl.formatMessage({
                id: "HomePageTemplateV2.tabsSection.label.tab6",
                defaultMessage: "IT"
            }),
            label: "IT",
            component: ITTabContent
        }
    ];

    return (
        <UFOSegment name="homepage-v2-tabs">
            <Adg4Tabs>
                <section id="tabs" css={sectionStyles}>
                    <Row>
                        <h2>
                            {intl.formatMessage({
                                id: "HomePageTemplateV2.tabsSection.header",
                                defaultMessage:
                                    "Empower everyone, on every team"
                            })}
                        </h2>
                    </Row>

                    <Adg4DraggableTabList className="tab-list">
                        {tabs.map((tab, index) => (
                            <Adg4DraggableTab key={index} className="round-tab">
                                <Tab
                                    title={tab.title}
                                    dataEventProps={{
                                        "data-product-key": "home",
                                        "data-event": "clicked",
                                        "data-event-component": "button",
                                        "data-event-container": "tabsMenu",
                                        "data-label": tab.title,
                                        "data-label-english": tab.label
                                    }}
                                />
                            </Adg4DraggableTab>
                        ))}
                    </Adg4DraggableTabList>
                    {tabs.map((tab, index) => (
                        <Adg4TabPanel key={index}>
                            {typeof tab.component === "function"
                                ? tab.component()
                                : tab.component}
                        </Adg4TabPanel>
                    ))}
                </section>
                <Integrations />
            </Adg4Tabs>
        </UFOSegment>
    );
};

const sectionStyles = css({
    "width": "100%",
    ".tab-list": {
        gap: token("space.150"),

        padding: "32px 16px 42px",

        [media.below.sm]: {
            paddingBlockEnd: "0"
        }
    },
    ".round-tab": {
        height: 56,
        borderRadius: 28,

        div: {
            minWidth: "70px"
        }
    },
    "h2": {
        width: "100%",
        textAlign: "center"
    },
    ".tabs-content-card .img-section img": {
        height: "auto"
    },

    // overrides to adg4 tab-panel component.
    // TODO: see about moving these to pattern
    ".tabs-content-card": {
        border: "none",
        boxShadow:
            "0px 0px 1px 0px rgba(9, 30, 66, 0.31), 0px 1px 1px 0px rgba(9, 30, 66, 0.25)",
        borderRadius: "6px"
    },
    ".homepage-tabs-content": {
        ".content-list": {
            "h3, p": {
                marginBlockEnd: "12px"
            },
            "p": {
                marginBlockStart: 0
            }
        },
        ".link-content-container": {
            fontWeight: "400",
            marginBlockStart: 0
        },
        ".tabs-content-card": {
            "h5, p": {
                height: "auto",
                marginBlockEnd: "8px"
            },
            "a": {
                marginBlockStart: 0
            }
        }
    },
    ".tab-content-header-wrapper": {
        paddingBlock: "15px"
    },
    // mobile pattern overrides
    // TODO: consolidate these with pattern
    [media.below.sm]: {
        ".link-content-container": {
            marginBlockStart: 0
        },
        ".tab-content-header-wrapper": {
            paddingBlock: "0",
            marginBlockStart: "42px"
        },

        ".tabs-content-card": {
            ".img-section": {
                maxWidth: "80%",
                margin: "0 auto"
            }
        },

        ".text-section": {
            h5: {
                marginBlockEnd: "8px"
            },
            p: {
                marginBlockEnd: "0"
            }
        },
        ".content-text": {
            "padding": "6px 0px 16px",
            "h4, p": {
                margin: "4px 0px 0"
            },
            "h3": {
                marginBlockEnd: "16px"
            }
        }
    }
});

// TODO: move all tabStyles to the adg4 pattern
const tabStyles = css({
    "backgroundColor": "#F0F1F2",
    "alignItems": "center",
    "justifyContent": "center",
    "boxSizing": "border-box",
    "display": "flex",
    "textAlign": "center",
    "textDecoration": "none",
    "height": 56,
    "borderRadius": 28,
    "paddingInlineEnd": 20,
    "paddingInlineStart": 20,
    "cursor": "pointer",
    "border": "2px solid #F0F1F2",
    "fontFamily": "var(--bxp-charlie-text-font-family)",
    "fontWeight": "400",
    "fontSize": "20px",
    ":hover": {
        boxShadow:
            "0px 0px 1px 0px rgba(9, 30, 66, 0.31), 0px 1px 1px 0px rgba(9, 30, 66, 0.25)"
    }
});

const currentStyle = css({
    border: "2px solid #1868DB",
    backgroundColor: "#E9F2FE"
});
